/**
 * Webinar gated content form functionality
 */

const WEBINAR_FORM_SELECTOR = '[data-webinar-form]';
const WEBINAR_OVERLAY_SELECTOR = '[data-webinar-overlay]';
const WEBINAR_FORM_SECTION_SELECTOR = '[data-webinar-form-section]';
const WEBINAR_MAIN_SELECTOR = '[data-webinar-main]';

/**
 * Check if we arrived via direct link with viewnow param
 * and clean up the gated UI elements
 */
const checkDirectView = (): void => {
  const urlParams = new URLSearchParams(window.location.search);

  if (!urlParams.has('viewnow')) {
    return;
  }

  // Remove overlay
  const overlay = document.querySelector(WEBINAR_OVERLAY_SELECTOR);
  if (overlay) {
    overlay.remove();
  }

  // Remove form section
  const formSection = document.querySelector(WEBINAR_FORM_SECTION_SELECTOR);
  if (formSection) {
    formSection.remove();
  }

  // Remove gated class from main
  const main = document.querySelector(WEBINAR_MAIN_SELECTOR);
  if (main) {
    main.classList.remove('single-webinar__main--gated');
  }
};

/**
 * Handle form submission - validates and redirects with viewnow param
 */
const handleFormSubmit = (form: HTMLFormElement, event: SubmitEvent): void => {
  event.preventDefault();
  event.stopPropagation();

  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const gaId = submitButton?.id || 'webinar-watch';

  // Basic validation
  const email = form.querySelector<HTMLInputElement>('[name="email"]');
  const name = form.querySelector<HTMLInputElement>('[name="name"]');
  const users = form.querySelector<HTMLSelectElement>('[name="users"]');

  if (!email?.value?.trim()) {
    email?.focus();
    return;
  }

  if (!name?.value?.trim()) {
    name?.focus();
    return;
  }

  if (!users?.value) {
    users?.focus();
    return;
  }

  // Disable submit button
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.innerHTML = 'Loading...';
  }

  // Build the redirect URL with viewnow parameter
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('viewnow', '');
  currentUrl.searchParams.set('webinarsuccess', gaId);

  // TODO: In production, POST the form data to your backend here before redirecting
  // const formData = new FormData(form);
  // await fetch('/api/webinar-form', { method: 'POST', body: formData });

  // Redirect to unlock the video
  window.location.href = currentUrl.toString();
};

/**
 * Initialize webinar form functionality
 */
export const initWebinarForm = (): void => {
  // Check for direct view first
  checkDirectView();

  const form = document.querySelector<HTMLFormElement>(WEBINAR_FORM_SELECTOR);

  if (!form) {
    return;
  }

  // Ensure form doesn't submit normally
  form.addEventListener('submit', (event) => {
    handleFormSubmit(form, event);
  });
};

/**
 * Initialize play button overlay for ungated webinar videos.
 * Clicking the play button replaces the thumbnail with a Vimeo iframe.
 */
export const initWebinarPlayButton = (): void => {
  const overlay = document.querySelector<HTMLElement>('[data-webinar-play-overlay]');
  if (!overlay) return;

  const wrapper = overlay.closest<HTMLElement>('[data-vimeo-id]');
  if (!wrapper) return;

  const vimeoId = wrapper.dataset.vimeoId;
  if (!vimeoId) return;

  const playBtn = overlay.querySelector<HTMLButtonElement>('[data-webinar-play-btn]');
  if (!playBtn) return;

  const startVideo = () => {
    // Guard against non-numeric IDs
    if (!/^\d+$/.test(vimeoId)) return;

    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&api=1&dnt=1`;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.title =
      wrapper
        .closest('[data-webinar-video]')
        ?.closest('article')
        ?.querySelector<HTMLElement>('.single-webinar__title')
        ?.textContent?.trim() || document.title;

    overlay.remove();
    wrapper.appendChild(iframe);
  };

  overlay.addEventListener('click', startVideo);
};
